/**
 * Legacy localStorage-backed template storage.
 *
 * This was the Tier 1 persistence layer. In Tier 2 the authoritative
 * store is the backend (via `useCustomTemplates`), but a user may still
 * have templates in localStorage from before Tier 2 deployed — the
 * migration dialog reads from here to upload them to the backend.
 *
 * After a successful migration the localStorage entries are cleared
 * and the sentinel key is set to `'migrated'` so the migration dialog
 * doesn't re-prompt.
 *
 * @module legacy-template-storage
 */
import {
  CustomTemplateSchema,
  type CustomTemplate,
} from '@shared/custom-templates/types'

export const LEGACY_STORAGE_KEY = 'pf-connect-custom-templates'
export const MIGRATION_STATE_KEY = 'pf-connect-custom-templates-migration-state'

export type MigrationState = 'pending' | 'migrated' | 'dismissed'

/**
 * Load templates from the legacy localStorage slot, Zod-validating each
 * entry. Invalid entries are dropped silently so the migration dialog
 * doesn't show garbage rows.
 */
export function loadLegacyTemplates(): CustomTemplate[] {
  if (typeof window === 'undefined') return []
  const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    const valid: CustomTemplate[] = []
    for (const entry of parsed) {
      const result = CustomTemplateSchema.safeParse(entry)
      if (result.success) valid.push(result.data)
    }
    return valid
  } catch {
    return []
  }
}

/**
 * Remove a single template from legacy storage after a successful
 * migration upload. Rewrites the whole array to keep behavior simple.
 */
export function removeLegacyTemplate(id: string): void {
  if (typeof window === 'undefined') return
  const existing = loadLegacyTemplates()
  const next = existing.filter((t): boolean => t.id !== id)
  try {
    window.localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(next))
  } catch {
    // Ignore — migration can retry next session.
  }
}

/**
 * Wipe the legacy storage entirely. Called after a full successful
 * migration so the key doesn't linger indefinitely.
 */
export function clearLegacyTemplates(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY)
  } catch {
    // Ignore.
  }
}

export function getMigrationState(): MigrationState {
  if (typeof window === 'undefined') return 'pending'
  const stored = window.localStorage.getItem(MIGRATION_STATE_KEY)
  if (stored === 'migrated' || stored === 'dismissed') return stored
  return 'pending'
}

export function setMigrationState(state: MigrationState): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(MIGRATION_STATE_KEY, state)
  } catch {
    // Ignore.
  }
}
